import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { useRef } from "react";
import {
  FolderGroup,
  FolderValidationResult,
  validateFoldersPathsWithBackend,
  validateMultipleFolders,
} from "helpers/validations/folderValidation";
import { groupFilesByRootFolder } from "helpers/utility/folderUtils";
import {
  setInvalidFolders,
  setValidationLoading,
} from "redux/CreateDataset/actions";
import {
  getInvalidFolders,
  getValidationLoading,
  getFoldersWithLocalSelections,
} from "redux/CreateDataset/selectors";
import VLToastComponent from "views/components/VLToastComponent";
import { FolderMetadata } from "types";
import pluralize from "pluralize";

interface ValidationState {
  validationResults: {
    validFolders: any[];
    invalidFolders: FolderMetadata[];
  };
  error: string | null;
}

const createInvalidFolder = (
  folder: FolderGroup & { validationResult: FolderValidationResult },
): FolderMetadata => {
  return {
    id: `invalid_${Date.now()}_${Math.random()}`,
    folder_name: folder.folderName,
    status: "invalid",
    invalid_reason: folder.validationResult.errors.join("; "),
    selected: false,
    metadata: null,
    total_images: 0,
    upload_percentage: 0,
    preview_urls: [],
    backend_status: "invalid",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

export default function useFolderValidation(batchId?: string) {
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const foldersWithLocalSelections = useSelector(getFoldersWithLocalSelections);
  const currentInvalidFolders = useSelector(getInvalidFolders);
  const isValidating = useSelector(getValidationLoading);
  const toastIdRef = useRef<any>(null);
  const [validationState, setValidationState] = useState<ValidationState>({
    validationResults: {
      validFolders: [],
      invalidFolders: [],
    },
    error: null,
  });

  const showErrorToast = (title: string, description: string) => {
    toastIdRef.current = toast(
      <VLToastComponent
        title={title}
        status="ERROR"
        description={description}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      { autoClose: 7000, closeButton: false, closeOnClick: false },
    );
  };

  const showDuplicateFolderError = useCallback((folderName: string) => {
    showErrorToast(
      "Folder already exists",
      `Folder "${folderName}" already exists in this dataset.`,
    );
  }, []);

  const validateFoldersPaths = async (paths: string[]) => {
    dispatch(setValidationLoading(true));
    const { invalid_folders } = await validateFoldersPathsWithBackend(
      newDatasetId!,
      paths,
      batchId,
    );
    const invalidFoldersCount = Object.keys(invalid_folders).length;
    if (invalidFoldersCount > 0) {
      showErrorToast(
        `The following ${pluralize("error", invalidFoldersCount)} occurred`,
        Object.entries(invalid_folders)
          .map(([path, message]) => `${path}: ${message}`)
          .join("\n"),
      );
    }
    dispatch(setValidationLoading(false));
  };

  const validateFolders = useCallback(
    async (files: File[]) => {
      if (!newDatasetId) {
        setValidationState((prev) => ({
          ...prev,
          error: "No dataset ID available",
        }));
        dispatch(setValidationLoading(false));
        return null;
      }

      dispatch(setValidationLoading(true));

      try {
        // Group files by their root folder
        const folderGroups = groupFilesByRootFolder(files);

        // Check for duplicate folder names against existing folders
        const existingFolderNames = [
          ...foldersWithLocalSelections,
          ...currentInvalidFolders,
        ].map((folder) => folder.folder_name);
        const duplicateFolders: string[] = [];

        folderGroups.forEach((folderGroup) => {
          if (existingFolderNames.includes(folderGroup.folderName)) {
            duplicateFolders.push(folderGroup.folderName);
            showDuplicateFolderError(folderGroup.folderName);
          }
        });

        // Filter out duplicate folders before validation
        const nonDuplicateFolderGroups = folderGroups.filter(
          (folderGroup) =>
            !existingFolderNames.includes(folderGroup.folderName),
        );

        // If all folders are duplicates, return early
        if (nonDuplicateFolderGroups.length === 0) {
          const results = { validFolders: [], invalidFolders: [] };

          setValidationState({ validationResults: results, error: null });

          dispatch(setValidationLoading(false));
          return results;
        }

        // Validate non-duplicate folders before sending to backend
        const { validFolders, invalidFolders } = await validateMultipleFolders(
          nonDuplicateFolderGroups,
          newDatasetId,
          dispatch,
          batchId,
        );

        // Store invalid folders in local state for display
        // if (invalidFolders.length > 0) {
        //   const invalidFoldersForState = invalidFolders.map((folder) =>
        //     createInvalidFolder(folder)
        //   );
        //
        //   // Filter out folders that already exist as invalid (by folder name)
        //   const newInvalidFolders = invalidFoldersForState.filter(
        //     (newFolder: FolderMetadata) =>
        //       !currentInvalidFolders.some(
        //         (existingFolder: FolderMetadata) =>
        //           existingFolder.folder_name === newFolder.folder_name
        //       )
        //   );
        //
        //   // Append only new invalid folders to existing ones
        //   if (newInvalidFolders.length > 0) {
        //     const combinedInvalidFolders = [
        //       ...currentInvalidFolders,
        //       ...newInvalidFolders,
        //     ];
        //     dispatch(setInvalidFolders(combinedInvalidFolders));
        //   }
        // }

        const results = {
          validFolders,
          invalidFolders:
            invalidFolders.length > 0
              ? invalidFolders.map((folder) => createInvalidFolder(folder))
              : [],
        };

        // setValidationState({
        //   validationResults: results,
        //   error: null,
        // });
        //
        dispatch(setValidationLoading(false));
        return results;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || "Validation failed";
        setValidationState({
          validationResults: { validFolders: [], invalidFolders: [] },
          error: errorMessage,
        });
        dispatch(setValidationLoading(false));
        return null;
      }
    },
    [
      newDatasetId,
      dispatch,
      foldersWithLocalSelections,
      currentInvalidFolders,
      batchId,
      showDuplicateFolderError,
    ],
  );

  const resetValidation = useCallback(() => {
    setValidationState({
      validationResults: { validFolders: [], invalidFolders: [] },
      error: null,
    });
  }, []);

  const clearInvalidFolders = useCallback(() => {
    dispatch(setInvalidFolders([]));
  }, [dispatch]);

  const removeInvalidFolder = useCallback(
    (folderName: string) => {
      const remainingInvalidFolders = currentInvalidFolders.filter(
        (folder: FolderMetadata) => folder.folder_name !== folderName,
      );
      dispatch(setInvalidFolders(remainingInvalidFolders));
    },
    [currentInvalidFolders, dispatch],
  );

  const setIsValidationLoading = (isLoading: boolean) => {
    dispatch(setValidationLoading(isLoading));
  };

  return {
    ...validationState,
    isValidating,
    validateFolders,
    resetValidation,
    clearInvalidFolders,
    removeInvalidFolder,
    setIsValidating: setIsValidationLoading,
    validateFoldersPaths,
  };
}
